import { LoadingModal } from "@mixitone/components";
import { Club, type ClubPlayer, ClubPlayerToken, Night } from "@mixitone/models";
import { ApplicationView } from "@mixitone/mvc";
import { isPresent, withStateToggle } from "@mixitone/util";
import clsx from "clsx";
import ClubPlayerForm from "components/Forms/ClubPlayerForm";
import { PlayerSearch } from "components/PlayerSearch";
import AppModal from "components/common/AppModal";
import MixitoneController from "controllers/MixitoneController";
import { injectModal } from "lib/injectParams";
import routeService from "services/RouteService";

interface Props {
  clubId: string;
  tokenId?: string;
  sessionId?: string;
  attributes?: Partial<ClubPlayer>;
}

interface State {
  club: Club;
  clubPlayers: ClubPlayer[];
  showSearch: boolean;
  loading: boolean;
  tokenId?: string;
  sessionId?: string;
}

class NewPlayerController extends MixitoneController<State, Props> {
  get initialState(): State {
    return {
      club: new Club({}),
      clubPlayers: [],
      showSearch: false,
      loading: true,
    };
  }

  async initialize(props: Props) {
    this.state.tokenId = props.tokenId;
    this.state.sessionId = props.sessionId;

    if (props.clubId) {
      this.state.club = await Club.query().find(props.clubId);
      await this.state.club.club_players.load();
      this.state.clubPlayers = this.state.club.club_players;
    }

    this.state.showSearch = isPresent(props.tokenId);
    this.state.loading = false;
  }

  @withStateToggle("loading")
  async actionAddTokenToPlayer(player: ClubPlayer) {
    const { club } = this.state;
    if (!club.id) {
      throw new Error("Club ID is required to add a player.");
    }

    if (this.state.tokenId) {
      const playerToken = new ClubPlayerToken({
        account_id: club.account_id,
        club_player_id: player.id,
        token_id: this.state.tokenId,
      });
      await playerToken.save();
    }

    this.actionCloseModal();
    await this.startOrAddToSession(player);
  }

  @withStateToggle("loading")
  async actionSubmit(player: ClubPlayer) {
    console.log("close modal");
    this.actionCloseModal();

    if (this.state.tokenId) {
      await this.startOrAddToSession(player);
    } else {
      await this.addToSession(player);
    }
  }

  async startOrAddToSession(player: ClubPlayer) {
    const { club } = this.state;
    if (!club.id) return;

    if (this.state.sessionId) {
      this.addToSession(player);
    } else {
      console.log("Starting new session for club", club.id, "with player", player.id);
      routeService.openModal("/clubs/:clubId/sessions/new", {
        params: { clubId: club.id, clubPlayerId: player.id },
      });
    }
  }

  async addToSession(player: ClubPlayer) {
    if (this.state.sessionId) {
      console.log("Adding player to session", this.state.sessionId);
      const session = await Night.query().findBy("id", this.state.sessionId);
      if (session) {
        await session.addClubPlayer(player);
      }
    }
  }
}

const NewPlayer: React.FC<Props> = ({ clubId, tokenId, sessionId, attributes }) => {
  const controller = NewPlayerController.use();
  const { clubPlayers, loading, showSearch } = controller.state;

  if (loading) return <LoadingModal title="New player" />;

  return (
    <AppModal title="New player">
      <div className={clsx({ "grid grid-cols-1 @sm/app:min-w-[550px] @sm/app:grid-cols-2": showSearch })}>
        {showSearch && (
          <div className="flex flex-col items-center justify-between @sm/app:pr-8">
            <div className="text-center font-semibold">Add NFC token to existing player</div>
            <PlayerSearch
              autoFocus
              onSelect={(player: ClubPlayer) => {
                controller.actionAddTokenToPlayer(player);
              }}
              clubPlayers={clubPlayers}
              canRegister={false}
              value={null}
            />
            <div>&nbsp;</div>
          </div>
        )}
        {showSearch && (
          <div className="z-10 translate-y-4 transform bg-white text-center text-sm font-semibold text-gray-500 @sm/app:absolute @sm/app:left-1/2 @sm/app:top-1/2 @sm/app:-translate-x-1/2 @sm/app:-translate-y-1/2">
            <div className="relative flex items-center">
              <div className="flex-grow border-t border-gray-300 @sm/app:hidden" />
              <span className="mx-2 flex-shrink bg-white px-2 text-gray-600 @sm/app:py-2">Or</span>
              <div className="flex-grow border-t border-gray-300 @sm/app:hidden" />
            </div>
          </div>
        )}
        <div
          className={clsx({
            "col-span-2 @sm/app:col-span-1": !showSearch,
            "border-t border-gray-200 pt-8 @sm/app:border-l @sm/app:border-t-0 @sm/app:pl-8 @sm/app:pt-0":
              showSearch,
          })}
        >
          {showSearch && <div className="mb-4 text-center font-semibold">Register new player</div>}
          <ClubPlayerForm
            clubId={clubId}
            tokenId={tokenId}
            sessionId={sessionId}
            attributes={attributes ?? {}}
            onSubmit={(player) => controller.actionSubmit(player)}
            autoFocus
          />
        </div>
      </div>
    </AppModal>
  );
};

export default injectModal(NewPlayerController.scope(ApplicationView(NewPlayer)));
